import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #detailsAnimation = null;

  #expanded = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#expanded = this.details.open;

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (!this.hasAttribute('data-animate')) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      event.preventDefault();
      this.#expanded = !this.#expanded;
      this.details.open = this.#expanded;
      this.classList.toggle('is-expanded', this.#expanded);
      this.classList.remove('is-collapsing');
      return;
    }

    event.preventDefault();
    this.#animateDetails();
  };

  /**
   * Smoothly expands or collapses the complete details card.
   */
  #animateDetails() {
    this.#expanded = !this.#expanded;
    const currentHeight = this.details.getBoundingClientRect().height;

    this.#detailsAnimation?.cancel();
    this.details.open = true;
    const targetHeight = this.#expanded ? this.details.scrollHeight : this.summary.getBoundingClientRect().height;
    const duration = this.#expanded ? 280 : 220;
    const easing = this.#expanded ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)';

    this.#detailsAnimation = this.details.animate(
      {
        height: [`${currentHeight}px`, `${targetHeight}px`],
      },
      {
        duration,
        easing,
      }
    );

    this.classList.toggle('is-collapsing', !this.#expanded);
    this.classList.toggle('is-expanded', this.#expanded);

    this.#detailsAnimation.onfinish = () => {
      if (!this.#expanded) this.details.open = false;
      this.classList.remove('is-collapsing');
      this.#detailsAnimation = null;
    };
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    this.#expanded = this.details.open;
    this.classList.toggle('is-expanded', this.#expanded);
    this.classList.remove('is-collapsing');
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.#expanded = false;
      this.classList.remove('is-expanded');
      this.classList.remove('is-collapsing');
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
